import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Button,
  Space,
  Typography,
  Select,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  Modal,
  message,
  Tag,
  Tooltip,
  Pagination,
  Input
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  CalendarOutlined,
  NotificationOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNotificaciones } from './NotificacionesContext';
import moment from 'moment';
import 'moment/locale/es';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

// Configurar moment en español
moment.locale('es');

const NotificacionesPage: React.FC = () => {
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [estadoFiltro, setEstadoFiltro] = useState<string>('');
  const [busqueda, setBusqueda] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [tamañoPagina] = useState(20);
  
  const {
    notificaciones,
    conteo,
    loading,
    marcarComoLeida,
    eliminar,
    marcarVariasComoLeidas,
    marcarTodasComoLeidas,
    cargarNotificaciones,
    refrescar
  } = useNotificaciones();

  // Cargar notificaciones cuando cambien los filtros
  useEffect(() => {
    const offset = (paginaActual - 1) * tamañoPagina;
    cargarNotificaciones(categoriaFiltro || undefined, tamañoPagina, offset);
  }, [categoriaFiltro, paginaActual, tamañoPagina, cargarNotificaciones]);

  const handleMarcarComoLeida = async (notificacionId: string) => {
    try {
      await marcarComoLeida(notificacionId);
      message.success('Notificación marcada como leída');
    } catch (error) {
      message.error('Error al marcar como leída');
    }
  };

  const handleEliminar = async (notificacionId: string) => {
    confirm({
      title: '¿Eliminar notificación?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await eliminar(notificacionId);
          message.success('Notificación eliminada');
        } catch (error) {
          message.error('Error al eliminar notificación');
        }
      }
    });
  };

  const handleMarcarTodasComoLeidas = async () => {
    confirm({
      title: '¿Marcar todas como leídas?',
      icon: <CheckOutlined />,
      content: 'Se marcarán todas las notificaciones visibles como leídas.',
      okText: 'Marcar todas',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await marcarTodasComoLeidas();
          message.success('Todas las notificaciones han sido marcadas como leídas');
        } catch (error) {
          message.error('Error al marcar notificaciones como leídas');
        }
      }
    });
  };

  const handleRefrescar = async () => {
    try {
      await refrescar();
      message.success('Notificaciones actualizadas');
    } catch (error) {
      message.error('Error al actualizar notificaciones');
    }
  };

  const getIconoCategoria = (categoria: string) => {
    switch (categoria) {
      case 'agenda':
        return <CalendarOutlined style={{ color: '#1890ff' }} />;
      case 'sistema':
        return <SettingOutlined style={{ color: '#722ed1' }} />;
      case 'recordatorio':
        return <NotificationOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#52c41a' }} />;
    }
  };

  const getColorPrioridad = (prioridad: string) => {
    switch (prioridad) {
      case 'alta':
        return '#ff4d4f';
      case 'baja':
        return '#1890ff';
      default:
        return '#52c41a';
    }
  };

  const getTagCategoria = (categoria: string) => {
    const configs = {
      general: { color: 'default', text: 'General' },
      agenda: { color: 'blue', text: 'Agenda' },
      sistema: { color: 'purple', text: 'Sistema' },
      recordatorio: { color: 'orange', text: 'Recordatorio' }
    };
    
    const config = configs[categoria as keyof typeof configs] || configs.general;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getTagPrioridad = (prioridad: string) => {
    const configs = {
      alta: { color: 'red', text: 'Alta' },
      normal: { color: 'green', text: 'Normal' },
      baja: { color: 'blue', text: 'Baja' }
    };
    
    const config = configs[prioridad as keyof typeof configs] || configs.normal;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Filtrar notificaciones por búsqueda y estado
  const notificacionesFiltradas = notificaciones.filter(notif => {
    const coincideBusqueda = !busqueda || 
      notif.titulo.toLowerCase().includes(busqueda.toLowerCase()) ||
      notif.descripcion.toLowerCase().includes(busqueda.toLowerCase());
    
    const coincideEstado = !estadoFiltro || 
      (estadoFiltro === 'leidas' && notif.leida) ||
      (estadoFiltro === 'no-leidas' && !notif.leida);
    
    return coincideBusqueda && coincideEstado;
  });

  const renderNotificacion = (notificacion: any) => (
    <List.Item
      key={notificacion.id}
      style={{
        padding: '16px 24px',
        borderLeft: `4px solid ${getColorPrioridad(notificacion.prioridad)}`,
        backgroundColor: notificacion.leida ? '#fafafa' : '#ffffff',
        marginBottom: '8px',
        borderRadius: '6px',
        border: '1px solid #f0f0f0'
      }}
      actions={[
        !notificacion.leida && (
          <Tooltip title="Marcar como leída">
            <Button 
              type="primary"
              ghost
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleMarcarComoLeida(notificacion.id)}
            >
              Marcar leída
            </Button>
          </Tooltip>
        ),
        <Tooltip title="Eliminar">
          <Button 
            type="primary"
            danger
            ghost
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleEliminar(notificacion.id)}
          >
            Eliminar
          </Button>
        </Tooltip>
      ].filter(Boolean)}
    >
      <List.Item.Meta
        avatar={getIconoCategoria(notificacion.categoria)}
        title={
          <div>
            <Space size={8} wrap>
              <Text strong={!notificacion.leida} style={{ fontSize: '16px' }}>
                {notificacion.titulo}
              </Text>
              {getTagCategoria(notificacion.categoria)}
              {getTagPrioridad(notificacion.prioridad)}
              {!notificacion.leida && (
                <Tag color="orange">No leída</Tag>
              )}
            </Space>
          </div>
        }
        description={
          <div style={{ marginTop: '8px' }}>
            <Text style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
              {notificacion.descripcion}
            </Text>
            <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {moment(notificacion.fechaCreacion).format('DD/MM/YYYY HH:mm')}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {moment(notificacion.fechaCreacion).fromNow()}
              </Text>
              {notificacion.leida && notificacion.fechaLectura && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Leída {moment(notificacion.fechaLectura).fromNow()}
                </Text>
              )}
            </Space>
          </div>
        }
      />
    </List.Item>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                <BellOutlined style={{ marginRight: '8px' }} />
                Notificaciones
              </Title>
            </Col>
            <Col flex="auto" />
            <Col>
              <Button 
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRefrescar}
                loading={loading}
              >
                Actualizar
              </Button>
            </Col>
          </Row>
        </div>

        {/* Estadísticas */}
        {conteo && (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Total" value={conteo.total} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic 
                  title="No leídas" 
                  value={conteo.noLeidas} 
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Agenda" value={conteo.porCategoria.agenda} />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="Sistema" value={conteo.porCategoria.sistema} />
              </Card>
            </Col>
          </Row>
        )}

        {/* Filtros y Acciones */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Input
              placeholder="Buscar notificaciones..."
              prefix={<SearchOutlined />}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Categoría"
              value={categoriaFiltro}
              onChange={setCategoriaFiltro}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="general">General</Option>
              <Option value="agenda">Agenda</Option>
              <Option value="sistema">Sistema</Option>
              <Option value="recordatorio">Recordatorio</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Estado"
              value={estadoFiltro}
              onChange={setEstadoFiltro}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="leidas">Leídas</Option>
              <Option value="no-leidas">No leídas</Option>
            </Select>
          </Col>
          <Col flex="auto" />
          <Col>
            {conteo && conteo.noLeidas > 0 && (
              <Button 
                type="primary"
                ghost
                icon={<CheckOutlined />}
                onClick={handleMarcarTodasComoLeidas}
              >
                Marcar todas como leídas
              </Button>
            )}
          </Col>
        </Row>

        {/* Lista de Notificaciones */}
        <Spin spinning={loading}>
          {notificacionesFiltradas.length === 0 ? (
            <Empty 
              description="No hay notificaciones"
              style={{ padding: '60px 0' }}
            />
          ) : (
            <>
              <List
                dataSource={notificacionesFiltradas}
                renderItem={renderNotificacion}
                split={false}
                style={{ minHeight: '400px' }}
              />
              
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Pagination
                  current={paginaActual}
                  pageSize={tamañoPagina}
                  total={conteo?.total || 0}
                  onChange={setPaginaActual}
                  showSizeChanger={false}
                  showQuickJumper
                  showTotal={(total, range) => 
                    `${range[0]}-${range[1]} de ${total} notificaciones`
                  }
                />
              </div>
            </>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default NotificacionesPage;
